/**
 * The Lab route's URL contract: which run, turn and comparison run are
 * selected lives in the query string so a view can be linked and survives a
 * reload. Everything read here is shape-checked before it can reach a query
 * key or a request path; anything else reads as "not selected".
 */

export const LAB_RUN_PARAM = "run";
export const LAB_TURN_PARAM = "turn";
export const LAB_COMPARE_PARAM = "compare";

export const LAB_TAB_PARAM = "tab";
/** Legacy (read only): the turn tab id of links made before `pane` existed. */
export const LAB_ITAB_PARAM = "itab";
export const LAB_PANE_PARAM = "pane";

/** The right pane's two scopes: "This turn" and "Whole conversation". */
export const LAB_PANES = ["turn", "run"] as const;
export type LabPaneId = (typeof LAB_PANES)[number];

export const LAB_PANE_LABELS = {
  turn: "This turn",
  run: "Whole conversation",
} as const satisfies Record<LabPaneId, string>;

/** One plain sentence per scope button, shown on hover and keyboard focus. */
export const LAB_PANE_HELP = {
  turn: "Details of the one turn you selected in the conversation.",
  run: "Views that cover the whole conversation, not just one turn.",
} as const satisfies Record<LabPaneId, string>;

/** Whole-run tabs, in display order ("compare" only while compare mode is on). */
export const LAB_RUN_TABS = [
  "state",
  "flow",
  "prompts",
  "safety",
  "raw",
  "overview",
  "timeline",
  "latency",
  "metrics",
  "services",
  "compare",
] as const;
export type LabRunTabId = (typeof LAB_RUN_TABS)[number];

export const LAB_RUN_TAB_LABELS = {
  overview: "Overview",
  state: "Current State",
  timeline: "Timeline",
  flow: "Flow",
  prompts: "Prompts",
  latency: "Latency & models",
  metrics: "Metrics",
  services: "Services",
  raw: "Raw",
  safety: "Safety",
  compare: "Compare",
} as const satisfies Record<LabRunTabId, string>;

/** One plain sentence per Whole conversation tab, shown on hover and keyboard focus. */
export const LAB_RUN_TAB_HELP = {
  state:
    "Where the conversation stands now, with flow steps and classification results.",
  flow: "The path the conversation took, as clickable steps that open each turn.",
  prompts: "Every prompt call in the conversation as one list you can filter.",
  safety:
    "Every patient message with its risk level and the checks on Liv's reply.",
  raw: "Every recorded step of the conversation in order, filterable by type and status.",
  overview:
    "Basic facts about the conversation, how complete its record is, and export options.",
  timeline:
    "How long each stage took, the moves between stages, and anything needing attention.",
  latency:
    "How long Liv's calls took by type and by turn, and which model answered.",
  metrics:
    "Summary numbers like turns, minutes, repeated replies and reply speed.",
  services:
    "The features this conversation used and every service call, turn by turn.",
  compare: "Conversation A and B side by side, with every difference flagged.",
} as const satisfies Record<LabRunTabId, string>;

export const LAB_TURN_TABS = [
  "summary",
  "state",
  "prompts",
  "safety",
  "classification",
  "criteria",
  "services",
] as const;
export type LabTurnTabId = (typeof LAB_TURN_TABS)[number];

export const LAB_TURN_TAB_LABELS = {
  summary: "Summary",
  state: "Current State",
  classification: "Classification",
  criteria: "Criteria",
  prompts: "Prompts",
  safety: "Safety",
  services: "Services",
} as const satisfies Record<LabTurnTabId, string>;

/** One plain sentence per This turn tab, shown on hover and keyboard focus. */
export const LAB_TURN_TAB_HELP = {
  summary:
    "A quick overview of how Liv classified, routed and safety-checked this turn.",
  state:
    "Where the conversation stands after this turn, with flow steps and classifications.",
  prompts: "How each prompt used on this turn was built from its parts.",
  safety: "Each safety check on this turn, what it found and what it did.",
  classification:
    "Each classification on this turn with its result, rationale and score.",
  criteria:
    "Each rule checked on this turn, its value, its limit and whether it passed.",
  services:
    "The extra services called on this turn and what each one returned.",
} as const satisfies Record<LabTurnTabId, string>;

/** Any id the `tab` param can hold: it names a tab of the pane it sits in. */
export type LabTabId = LabRunTabId | LabTurnTabId;

export const DEFAULT_LAB_RUN_TAB: LabRunTabId = "state";
export const DEFAULT_LAB_TURN_TAB: LabTurnTabId = "summary";

/**
 * `pane` / `tab` are null when absent or unknown; readers apply the defaults
 * (see `effectivePane` / `effectiveTab` in labLayout). Old links still open:
 * `tab=<run tab>` selects the Whole conversation pane, `itab=` the This turn pane,
 * `tab=conversation` is dropped (the conversation is always visible).
 */
export type LabSelection = {
  run: string | null;
  turn: number | null;
  compare: string | null;
  pane: LabPaneId | null;
  tab: LabTabId | null;
};

const RUN_REF_PATTERN = /^pvs_[0-9a-f]{16,64}$/;
const MAX_TURN = 10_000;

function readRef(value: string | null | undefined): string | null {
  return typeof value === "string" && RUN_REF_PATTERN.test(value) ? value : null;
}

function readTurn(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  const text = typeof value === "number" ? String(value) : value;
  if (!/^\d+$/.test(text)) {
    return null;
  }
  const turn = Number(text);
  return Number.isSafeInteger(turn) && turn >= 1 && turn <= MAX_TURN ? turn : null;
}

function readPane(value: string | null | undefined): LabPaneId | null {
  return (LAB_PANES as readonly (string | null | undefined)[]).includes(value)
    ? (value as LabPaneId)
    : null;
}

function readTurnTab(value: string | null | undefined): LabTurnTabId | null {
  if (value === "assembly") {
    return "prompts"; // the old "Prompt assembly" tab is now "Prompts"
  }
  return (LAB_TURN_TABS as readonly (string | null | undefined)[]).includes(value)
    ? (value as LabTurnTabId)
    : null;
}

function readRunTab(value: string | null | undefined): LabRunTabId | null {
  return (LAB_RUN_TABS as readonly (string | null | undefined)[]).includes(value)
    ? (value as LabRunTabId)
    : null;
}

function readTab(value: string | null | undefined): LabTabId | null {
  return readRunTab(value) ?? readTurnTab(value);
}

/**
 * Shape-checks run/compare against `pvs_<16-64 hex>` (anything else is null),
 * turn must be an integer 1..10000, and a compare equal to the run is null.
 */
export function readLabSelection(params: URLSearchParams): LabSelection {
  const run = readRef(params.get(LAB_RUN_PARAM));
  const compare = readRef(params.get(LAB_COMPARE_PARAM));
  let pane = readPane(params.get(LAB_PANE_PARAM));
  let tab = readTab(params.get(LAB_TAB_PARAM));
  const legacyTurnTab = readTurnTab(params.get(LAB_ITAB_PARAM));
  if (pane === null) {
    // Links made before `pane` existed: the tab names the pane.
    const rawTab = params.get(LAB_TAB_PARAM);
    if (readRunTab(rawTab) !== null) {
      pane = "run";
    } else if (readTurnTab(rawTab) !== null || legacyTurnTab !== null) {
      pane = "turn";
    }
  }
  if (tab === null && pane === "turn") {
    tab = legacyTurnTab;
  }
  return {
    run,
    turn: readTurn(params.get(LAB_TURN_PARAM)),
    compare: compare !== run ? compare : null,
    pane,
    tab,
  };
}

/**
 * A NEW URLSearchParams: `current` copied (unknown params preserved) with
 * `patch` applied. A key present with null deletes; an invalid value is
 * dropped, never written. Changing the run clears the turn unless the patch
 * names one; a run equal to the current compare clears the compare.
 */
export function writeLabSelection(
  current: URLSearchParams,
  patch: Partial<LabSelection>,
): URLSearchParams {
  const next = new URLSearchParams(current);

  if (patch.run !== undefined) {
    const run = patch.run === null ? null : readRef(patch.run);
    if (patch.run === null || run !== null) {
      if (run !== next.get(LAB_RUN_PARAM)) {
        if (patch.turn === undefined) {
          next.delete(LAB_TURN_PARAM);
        }
        if (run !== null && run === next.get(LAB_COMPARE_PARAM) && patch.compare === undefined) {
          next.delete(LAB_COMPARE_PARAM);
        }
      }
      if (run === null) {
        next.delete(LAB_RUN_PARAM);
      } else {
        next.set(LAB_RUN_PARAM, run);
      }
    }
  }

  if (patch.turn !== undefined) {
    const turn = readTurn(patch.turn);
    if (patch.turn === null) {
      next.delete(LAB_TURN_PARAM);
    } else if (turn !== null) {
      next.set(LAB_TURN_PARAM, String(turn));
    }
  }

  if (patch.compare !== undefined) {
    const compare = patch.compare === null ? null : readRef(patch.compare);
    if (patch.compare === null) {
      next.delete(LAB_COMPARE_PARAM);
    } else if (compare !== null && compare !== next.get(LAB_RUN_PARAM)) {
      next.set(LAB_COMPARE_PARAM, compare);
    }
  }

  if (patch.pane !== undefined) {
    const pane = patch.pane === null ? null : readPane(patch.pane);
    if (pane === null) {
      if (patch.pane === null) next.delete(LAB_PANE_PARAM);
    } else {
      next.set(LAB_PANE_PARAM, pane);
    }
  }
  if (patch.tab !== undefined) {
    const tab = patch.tab === null ? null : readTab(patch.tab);
    if (tab === null) {
      if (patch.tab === null) next.delete(LAB_TAB_PARAM);
    } else {
      next.set(LAB_TAB_PARAM, tab);
    }
  }
  if (patch.pane !== undefined || patch.tab !== undefined) {
    next.delete(LAB_ITAB_PARAM); // superseded by pane + tab
  }

  return next;
}

/** `/projects/<id>/lab` plus `?run=&turn=&compare=&pane=&tab=` for the non-null, valid parts of `selection`. */
export function labPath(projectId: string, selection: Partial<LabSelection> = {}): string {
  const base = `/projects/${encodeURIComponent(projectId)}/lab`;
  const run = readRef(selection.run);
  const turn = readTurn(selection.turn);
  const compare = readRef(selection.compare);
  const params = new URLSearchParams();
  if (run !== null) {
    params.set(LAB_RUN_PARAM, run);
  }
  if (turn !== null) {
    params.set(LAB_TURN_PARAM, String(turn));
  }
  if (compare !== null && compare !== run) {
    params.set(LAB_COMPARE_PARAM, compare);
  }
  const pane = readPane(selection.pane);
  if (pane !== null) {
    params.set(LAB_PANE_PARAM, pane);
  }
  const tab = readTab(selection.tab);
  if (tab !== null) {
    params.set(LAB_TAB_PARAM, tab);
  }
  const query = params.toString();
  return query === "" ? base : `${base}?${query}`;
}

/** The "Back to project" target. */
export function labBackPath(projectId: string): string {
  return `/projects/${encodeURIComponent(projectId)}`;
}
